import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  ValidationErrors,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {environment} from '../../../../environments/environment';
import {Subscription} from 'rxjs';
import {LanguageSelectComponent} from './language-select/language-select.component';
import {Constants} from '../../../globals';
import {countInvalidFields} from '../form-messages';
import {TITLECARD_MAX_LENGTH, TITLECARD_LANG} from '../titlecard/titlecard';


const notBlank = (c: AbstractControl): ValidationErrors | null =>
  (c.value || '').trim() === '' ? {required: true} : null;

@Component({
    selector: 'app-track-selection',
    templateUrl: './track-selection.component.html',
    styleUrls: ['./track-selection.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => TrackSelectionComponent),
        }, {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: forwardRef(() => TrackSelectionComponent),
        }],
  imports: [LanguageSelectComponent, ReactiveFormsModule]
})
export class TrackSelectionComponent implements OnInit, OnChanges, ControlValueAccessor, OnDestroy {
  static DEFAULTS = {
    audio: [],
    subtitles: [],
    cc: [],
    burntin: null,  // Format: "lang:type" e.g. "en:subtitle" or "en:closedcaption"
    titlecard: false,  // VTK-3398: burn a title card into the first 5 s instead of a subtitle
    titlecard_text: '',
  };
  /**
   * Whether the selected format's template can burn in a text track, from the workflow API's
   * template_capabilities (vtkpy). null while unknown: neither burnt-in option is offered then.
   */
  @Input() burnInSupported: boolean | null = null;
  /** The title card's starting text (from the source folder name), used when the option is switched on. */
  @Input() titlecardPrefill = '';
  readonly titlecardMaxLength = TITLECARD_MAX_LENGTH;
  readonly TITLECARD_OPTION = `${TITLECARD_LANG}:subtitle`;
  form: FormGroup;
  languages = Constants.languages;
  // Build burntin options: each language appears twice (as subtitle and closedcaption)
  burntinOptions: {value: string, label: string}[] = [
    {value: this.TITLECARD_OPTION, label: 'Title card (first 5 s, bottom left)'},
    ...Constants.languages.flatMap(lang => [
      {value: `${lang.id}:subtitle`, label: `${lang.name} (sub)`},
      {value: `${lang.id}:closedcaption`, label: `${lang.name} (cc)`},
    ])
  ];

  private changed;
  private touched;

  private sub: Subscription;

  protected readonly environment = environment;

  writeValue(value) {
    if (value?.titlecard && !value.burntin) {
      value = {...value, burntin: this.TITLECARD_OPTION};
    }
    this.form.patchValue(value || {}, {emitEvent: false});
  }

  registerOnChange(fn) {
    this.changed = fn;
  }

  registerOnTouched(fn) {
    this.touched = fn;
  }

  constructor(private formBuilder: FormBuilder) {
    console.log('constructing TrackSelectionComponent')
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      audio: ['', [Validators.nullValidator]],
      subtitles: ['', [Validators.nullValidator]],
      cc: ['', [Validators.nullValidator]],
      burntin: [null, [Validators.nullValidator]],
      titlecard: [false],
      titlecard_text: ['', [Validators.maxLength(TITLECARD_MAX_LENGTH)]],
      images: ['', [Validators.nullValidator]],
    });

    this.form.controls['burntin'].valueChanges.subscribe(val => {
      const isTitlecard = val === this.TITLECARD_OPTION;
      this.form.controls['titlecard'].setValue(isTitlecard, {emitEvent: false});
      this.applyTitlecard(isTitlecard);
    });
    this.applyBurnInSupport();
    // getRawValue: a disabled control (burntin while the title card is on) still reports its value (null).
    this.sub = this.form.valueChanges.subscribe(() => {
      this.touched();
      const val = this.form.getRawValue();
      if (val.burntin === this.TITLECARD_OPTION) {
        val.titlecard = true;
        val.burntin = null;
      } else {
        val.titlecard = false;
      }
      this.changed(val);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['burnInSupported'] && this.form) {
      this.applyBurnInSupport();
    }
  }

  private applyBurnInSupport(): void {
    if (!this.burnInSupported) {
      // The template cannot burn anything in (or it is not known yet): no burnt-in request may go out.
      this.form.patchValue({burntin: null, titlecard: false}, {emitEvent: false});
      this.applyTitlecard(false);
    }
  }

  private applyTitlecard(on: boolean): void {
    const text = this.form.controls['titlecard_text'];
    if (on) {
      if (!text.value) {
        text.setValue(this.titlecardPrefill || '', {emitEvent: false});
      }
      text.setValidators([Validators.required, Validators.maxLength(TITLECARD_MAX_LENGTH), notBlank]);
    } else {
      text.setValidators([Validators.maxLength(TITLECARD_MAX_LENGTH)]);
    }
    text.updateValueAndValidity({emitEvent: false});
    if (this.changed) {
      const val = this.form.getRawValue();
      if (val.burntin === this.TITLECARD_OPTION) {
        val.titlecard = true;
        val.burntin = null;
      } else {
        val.titlecard = false;
      }
      this.changed(val);
    }
  }

  validate() {
    return this.form && this.form.invalid ? {invalid: true, invalidFields: countInvalidFields(this.form)} : null;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
